/**
 * SessionSelector
 *
 * Dropdown UI mounted in the chat header. Lists sessions filtered by either
 * the active project (default) or all of the user's projects. Supports
 * switching session, starting a new chat for the active project, renaming
 * and deleting. Stays in sync with chatService + projectWatcher events.
 */

/* global document, console, window */

import { chatService } from "../../services/chatService.js";
import { projectWatcher } from "../../services/projectWatcher.js";

const FILTER_STORAGE_KEY = "shibumi.sessionSelector.filter";

function formatRelative(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffMs = Date.now() - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function escapeHTML(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export class SessionSelector {
  constructor(root) {
    this.toggleBtn = root.querySelector("#sessionSelectorToggle");
    this.titleEl = root.querySelector(".session-selector-current-title");
    this.panel = root.querySelector("#sessionSelectorPanel");
    this.listEl = root.querySelector("#sessionList");
    this.newChatBtn = root.querySelector("#newChatBtn");
    this.projectLabel = root.querySelector("#activeProjectLabel");
    this.filterButtons = Array.from(root.querySelectorAll(".filter-chip-btn"));

    this.filter = this._readSavedFilter();
    this.sessions = [];
    this._unsubscribers = [];
    this._isLoading = false;
    this._mounted = false;
    this._accountGeneration = 0;
    // When a _loadSessions() call arrives mid-flight we want to RE-RUN
    // after the current one finishes — not silently drop the request.
    // The earlier early-return left the UI showing the stale list after
    // delete because deleteSessionById() emits "delete" which triggers
    // _handleSessionChange()._loadSessions() FIRST, then the click handler's
    // await this._loadSessions() races in while the first is still in
    // flight and got dropped. Result: deleted chat stuck on screen.
    this._loadPending = false;
  }

  mount() {
    // Idempotency guard: mount() wires DOM listeners + subscribes to
    // chatService/projectWatcher. A second call (e.g. a caller that loses
    // track of whether it already mounted) would double-add every listener
    // and stack duplicate subscriptions in _unsubscribers, each firing on
    // every future event. This function's only current caller already
    // guards it externally, but that shouldn't be the only thing standing
    // between a double-mount and duplicated UI behavior.
    if (this._mounted) return;
    if (!this.toggleBtn || !this.panel) {
      console.warn("SessionSelector: required DOM not found");
      return;
    }
    this._mounted = true;

    this._applyFilterButtons();

    this.toggleBtn.addEventListener("click", () => this._togglePanel());
    this.newChatBtn?.addEventListener("click", () => this._handleNewChat());
    this.filterButtons.forEach((btn) =>
      btn.addEventListener("click", () => this._setFilter(btn.dataset.filter))
    );
    document.addEventListener("click", (e) => this._handleDocumentClick(e));

    this._unsubscribers.push(
      chatService.onSessionChange(() => this._handleSessionChange())
    );
    this._unsubscribers.push(
      projectWatcher.subscribe(({ name }) => {
        this._renderProjectLabel(name);
        this._loadSessions();
      })
    );

    this._renderProjectLabel(projectWatcher.current.name);
    this._loadSessions();
  }

  unmount() {
    this._unsubscribers.forEach((fn) => fn());
    this._unsubscribers = [];
    this._mounted = false;
  }

  resetForAccountChange() {
    this._accountGeneration += 1;
    this._loadPending = false;
    this.sessions = [];
    this._closePanel();
    this._renderProjectLabel(null);
    this._render();
  }

  refresh() {
    if (this._mounted) this._loadSessions();
  }

  _togglePanel() {
    const isOpen = !this.panel.hidden;
    if (isOpen) {
      this._closePanel();
    } else {
      this._openPanel();
    }
  }

  _openPanel() {
    this.panel.hidden = false;
    this.toggleBtn.setAttribute("aria-expanded", "true");
    this._loadSessions();
  }

  _closePanel() {
    this.panel.hidden = true;
    this.toggleBtn.setAttribute("aria-expanded", "false");
  }

  _handleDocumentClick(e) {
    if (this.panel.hidden) return;
    if (this.panel.contains(e.target) || this.toggleBtn.contains(e.target)) return;
    this._closePanel();
  }

  _setFilter(filter) {
    if (filter !== "project" && filter !== "all") return;
    this.filter = filter;
    try {
      window.localStorage.setItem(FILTER_STORAGE_KEY, filter);
    } catch (_) {
      // ignore — preference is a nice-to-have, not load-bearing
    }
    this._applyFilterButtons();
    this._loadSessions();
  }

  _readSavedFilter() {
    try {
      const v = window.localStorage.getItem(FILTER_STORAGE_KEY);
      return v === "all" ? "all" : "project";
    } catch (_) {
      return "project";
    }
  }

  _applyFilterButtons() {
    this.filterButtons.forEach((btn) => {
      btn.classList.toggle("active", btn.dataset.filter === this.filter);
    });
  }

  _renderProjectLabel(name) {
    if (!this.projectLabel) return;
    this.projectLabel.textContent = name ? `📁 ${name}` : "";
  }

  async _loadSessions() {
    if (this._isLoading) {
      // Mark that another load should run AFTER the in-flight one finishes.
      // Without this, the second caller silently drops and the UI ends up
      // showing whatever the FIRST (earlier) load saw — stale.
      this._loadPending = true;
      return;
    }
    this._isLoading = true;
    try {
      do {
        this._loadPending = false;
        const requestGeneration = this._accountGeneration;
        const projectGuid =
          this.filter === "project" ? projectWatcher.current.guid : null;
        try {
          const sessions = await chatService.listSessions({
            projectGuid,
            pageSize: 50,
          });
          if (requestGeneration === this._accountGeneration) {
            this.sessions = Array.isArray(sessions) ? sessions : [];
            this._render();
          }
        } catch (err) {
          if (requestGeneration === this._accountGeneration) {
            console.error("SessionSelector: load failed", err);
            this.listEl.innerHTML = `<div class="session-empty">Couldn't load sessions</div>`;
          }
        }
      } while (this._loadPending);
    } finally {
      this._isLoading = false;
    }
  }

  _handleSessionChange() {
    this._updateCurrentTitle();
    this._loadSessions();
  }

  _updateCurrentTitle() {
    const current = this.sessions.find(
      (s) => s.session_id === chatService.currentSessionId
    );
    this.titleEl.textContent = current?.title || "New chat";
  }

  _render() {
    if (!this.sessions.length) {
      this.listEl.innerHTML = `<div class="session-empty">No sessions yet</div>`;
      this._updateCurrentTitle();
      return;
    }

    const showProjectName = this.filter === "all";
    const rows = this.sessions
      .map((s) => {
        const isCurrent = s.session_id === chatService.currentSessionId;
        const projectChip =
          showProjectName && s.project_name
            ? `<div class="session-row-project">${escapeHTML(s.project_name)}</div>`
            : "";
        return `
          <div class="session-row ${isCurrent ? "current" : ""}" data-id="${escapeHTML(
            s.session_id
          )}">
            <div class="session-row-main">
              <div class="session-row-title">${escapeHTML(s.title || "Untitled")}</div>
              <div class="session-row-meta">
                <span>${escapeHTML(formatRelative(s.updated_at))}</span>
                ${projectChip}
              </div>
            </div>
            <div class="session-row-actions">
              <button class="session-action-btn rename-btn" title="Rename" data-action="rename">✎</button>
              <button class="session-action-btn delete-btn" title="Delete" data-action="delete">🗑</button>
            </div>
          </div>`;
      })
      .join("");
    this.listEl.innerHTML = rows;

    this.listEl.querySelectorAll(".session-row").forEach((row) => {
      const id = row.getAttribute("data-id");
      row.addEventListener("click", (e) => {
        const actionBtn = e.target.closest(".session-action-btn");
        if (actionBtn) {
          e.stopPropagation();
          this._handleRowAction(actionBtn.dataset.action, id);
        } else {
          this._handleSwitch(id);
        }
      });
    });

    this._updateCurrentTitle();
  }

  async _handleSwitch(sessionId) {
    try {
      // Re-selecting the session already in view reloads it from the backend
      // (switchToSession would no-op). This is how a user retrieves an answer
      // that finished while they were elsewhere, or was cut mid-stream.
      if (sessionId && sessionId === chatService.currentSessionId) {
        await chatService.reloadCurrentSession();
      } else {
        await chatService.switchToSession(sessionId);
      }
      this._closePanel();
    } catch (err) {
      console.error("SessionSelector: switch failed", err);
    }
  }

  async _handleNewChat() {
    try {
      await chatService.startNewChatForActiveProject();
      this._closePanel();
    } catch (err) {
      console.error("SessionSelector: new chat failed", err);
      alert(err.message || "Couldn't start a new chat");
    }
  }

  async _handleRowAction(action, sessionId) {
    if (action === "rename") {
      const current = this.sessions.find((s) => s.session_id === sessionId);
      const row = this.listEl.querySelector(`.session-row[data-id="${sessionId}"]`);
      if (!row) return;

      // Inline rename input (window.prompt is blocked in WebView2 task panes)
      const titleEl = row.querySelector(".session-row-title");
      const oldTitle = current?.title || "Untitled";
      titleEl.innerHTML = `<input class="session-rename-input" type="text" value="${escapeHTML(oldTitle)}" />`;
      const input = titleEl.querySelector("input");
      input.focus();
      input.select();

      const commit = async () => {
        const newTitle = input.value.trim();
        if (newTitle && newTitle !== oldTitle) {
          try {
            await chatService.renameSession(sessionId, newTitle);
          } catch (err) {
            console.error("rename failed", err);
          }
        }
        await this._loadSessions();
      };
      input.addEventListener("keydown", (e) => {
        if (e.key === "Enter") { e.preventDefault(); commit(); }
        if (e.key === "Escape") { this._loadSessions(); }
      });
      input.addEventListener("blur", () => commit());
      return;
    }
    if (action === "delete") {
      const row = this.listEl.querySelector(`.session-row[data-id="${sessionId}"]`);
      if (!row) return;

      // Inline confirmation (window.confirm is blocked in WebView2 task panes)
      const actionsEl = row.querySelector(".session-row-actions");
      actionsEl.innerHTML = `
        <span class="delete-confirm-label">Delete?</span>
        <button class="session-action-btn delete-yes" title="Yes">✓</button>
        <button class="session-action-btn delete-no" title="Cancel">✕</button>
      `;
      actionsEl.querySelector(".delete-yes").addEventListener("click", async (e) => {
        e.stopPropagation();
        // Optimistically remove from local state + DOM so the row disappears
        // instantly even if the backend round-trip is slow OR a concurrent
        // _loadSessions returns the stale list before the DELETE commits.
        // The subsequent _loadSessions reconciles against backend truth.
        this.sessions = this.sessions.filter((s) => s.session_id !== sessionId);
        row.remove();
        try {
          await chatService.deleteSessionById(sessionId);
        } catch (err) {
          console.error("delete failed", err);
        }
        await this._loadSessions();
      });
      actionsEl.querySelector(".delete-no").addEventListener("click", (e) => {
        e.stopPropagation();
        this._loadSessions();
      });
    }
  }
}
